import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../components/Profile.css';
import {
  buildCommunityChannelOptions,
  COMMUNITY_CHANNEL_OPTIONS,
  TEAM_PERMISSION_PRESETS,
  TEAM_PERMISSION_SECTIONS,
  DEFAULT_COLLABORATOR_PERMISSIONS,
  buildInvitePayload,
  normalizeCommunityChannels,
  normalizeDepartmentList,
  normalizeMemberRecord,
  normalizePermissionState,
  togglePermission,
  getPresetPermissions
} from '../../utils/teamAccess';

const EMPTY_INITIAL_VALUES = {};

function buildInitialForm(initialValues = {}) {
  const member = normalizeMemberRecord(initialValues, {
    permissions: DEFAULT_COLLABORATOR_PERMISSIONS
  });

  return {
    email: member.email,
    firstName: member.firstName,
    lastName: member.lastName,
    position: member.position,
    departmentScope: member.departmentScope,
    departments: member.departments,
    permissions: normalizePermissionState(member.permissions),
    communityChannels: normalizeCommunityChannels(member.communityChannels)
  };
}

function getPresetIdForPermissions(permissions = {}) {
  const normalized = normalizePermissionState(permissions);
  const matchingPreset = TEAM_PERMISSION_PRESETS.find((preset) =>
    JSON.stringify(normalizePermissionState(preset.permissions)) === JSON.stringify(normalized)
  );

  return matchingPreset?.id || 'custom';
}

const InviteMemberModal = ({
  isOpen,
  onClose,
  onSubmit,
  onInvite,
  isLoading,
  mode = 'invite',
  initialValues = EMPTY_INITIAL_VALUES,
  departmentOptions = [],
  communityChannelOptions = COMMUNITY_CHANNEL_OPTIONS
}) => {
  useUiLanguage();
  const { t } = useTranslation('profile');
  const [form, setForm] = useState(buildInitialForm(initialValues));
  const [activePreset, setActivePreset] = useState('custom');
  const [showCustomPermissions, setShowCustomPermissions] = useState(false);
  const submitAction = onSubmit || onInvite;
  const isEditMode = mode === 'edit';

  useEffect(() => {
    if (!isOpen) return;
    const nextForm = buildInitialForm(initialValues);
    setForm(nextForm);
    setActivePreset(getPresetIdForPermissions(nextForm.permissions));
    setShowCustomPermissions(getPresetIdForPermissions(nextForm.permissions) === 'custom');
  }, [initialValues, isOpen]);

  const normalizedDepartmentOptions = useMemo(
    () => normalizeDepartmentList(departmentOptions),
    [departmentOptions]
  );
  const normalizedCommunityChannelOptions = useMemo(
    () => buildCommunityChannelOptions(
      (communityChannelOptions || []).reduce((acc, option) => {
        acc[option.value] = { label: option.label, enabled: true };
        return acc;
      }, {})
    ),
    [communityChannelOptions]
  );

  if (!isOpen) return null;

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDepartmentScopeChange = (departmentScope) => {
    setForm((prev) => ({
      ...prev,
      departmentScope,
      departments: departmentScope === 'all' ? [] : prev.departments
    }));
  };

  const handleDepartmentToggle = (department) => {
    setForm((prev) => {
      const hasDepartment = prev.departments.includes(department);
      const departments = hasDepartment
        ? prev.departments.filter((entry) => entry !== department)
        : [...prev.departments, department];

      return {
        ...prev,
        departmentScope: departments.length > 0 ? 'specific' : prev.departmentScope,
        departments: normalizeDepartmentList(departments)
      };
    });
  };

  const handlePresetSelect = (presetId) => {
    const nextPermissions = getPresetPermissions(presetId);
    setActivePreset(presetId);
    setShowCustomPermissions(presetId === 'custom');
    setForm((prev) => ({
      ...prev,
      permissions: nextPermissions,
      communityChannels: nextPermissions.canEditCommunity ? prev.communityChannels : []
    }));
  };

  const handlePermissionToggle = (key) => {
    setActivePreset('custom');
    setForm((prev) => {
      const nextPermissions = togglePermission(prev.permissions, key);

      return {
        ...prev,
        permissions: nextPermissions,
        communityChannels: nextPermissions.canEditCommunity ? prev.communityChannels : []
      };
    });
  };

  const handleCommunityChannelToggle = (channel) => {
    setForm((prev) => {
      const hasChannel = prev.communityChannels.includes(channel);
      const communityChannels = hasChannel
        ? prev.communityChannels.filter((entry) => entry !== channel)
        : [...prev.communityChannels, channel];

      return {
        ...prev,
        communityChannels: normalizeCommunityChannels(communityChannels)
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!submitAction) return;
    submitAction(buildInvitePayload(form));
  };

  const canSubmit = Boolean(
    (isEditMode || form.email.trim()) &&
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.position.trim() &&
    (form.departmentScope === 'all' || form.departments.length > 0) &&
    (!form.permissions.canEditCommunity || form.communityChannels.length > 0)
  );

  return (
    <div className="invite-modal-overlay" onClick={onClose}>
      <div className="invite-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="invite-modal-header">
          <div>
            <h2>{isEditMode ? uiText("Manage Team Member Access") : uiText("Invite Team Member")}</h2>
            <p>{uiText("Set up identity, department scope, and permissions.")}</p>
          </div>
          <button type="button" className="invite-modal-close" onClick={onClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="invite-form">
          <div className="invite-modal-body">
            {/* Identity Section */}
            <div className="invite-section">
              <h3>{uiText("Identity")}</h3>
              <div className="invite-field-grid">
                <div className="invite-field">
                  <label>{uiText("First Name")}</label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => handleFieldChange('firstName', e.target.value)}
                    placeholder={uiText("Lina")}
                    required
                    className="invite-input"
                  />
                </div>
                <div className="invite-field">
                  <label>{uiText("Last Name")}</label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => handleFieldChange('lastName', e.target.value)}
                    placeholder={uiText("Haddad")}
                    required
                    className="invite-input"
                  />
                </div>
              </div>
              <div className="invite-field-grid">
                <div className="invite-field">
                  <label>{uiText("Email Address")}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder="colleague@company.com"
                    required
                    disabled={isEditMode}
                    className="invite-input"
                  />
                </div>
                <div className="invite-field">
                  <label>{uiText("Job Position")}</label>
                  <input
                    type="text"
                    value={form.position}
                    onChange={(e) => handleFieldChange('position', e.target.value)}
                    placeholder={uiText("Senior Recruiter")}
                    required
                    className="invite-input"
                  />
                </div>
              </div>
            </div>

            {/* Department Scope Section */}
            <div className="invite-section">
              <h3>{uiText("Department Scope")}</h3>
              <div className="invite-scope-toggle">
                <button
                  type="button"
                  className={`invite-scope-chip ${form.departmentScope === 'all' ? 'active' : ''}`}
                  onClick={() => handleDepartmentScopeChange('all')}
                >{uiText("All departments")}</button>
                <button
                  type="button"
                  className={`invite-scope-chip ${form.departmentScope === 'specific' ? 'active' : ''}`}
                  onClick={() => handleDepartmentScopeChange('specific')}
                >{uiText("Specific departments")}</button>
              </div>

              {form.departmentScope === 'specific' && (
                <div className="invite-department-picker">
                  {normalizedDepartmentOptions.length > 0 ? (
                    <div className="invite-chip-grid">
                      {normalizedDepartmentOptions.map((department) => (
                        <button
                          key={department}
                          type="button"
                          className={`invite-department-chip ${form.departments.includes(department) ? 'active' : ''}`}
                          onClick={() => handleDepartmentToggle(department)}
                        >
                          {uiText(department)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="invite-helper-text">{uiText("No department suggestions found. Create jobs first or use \"All departments\".")}</p>
                  )}
                </div>
              )}
            </div>

            {/* Permissions Section */}
            <div className="invite-section">
              <h3>{uiText("Permissions")}</h3>
              <div className="invite-preset-grid">
                {TEAM_PERMISSION_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`invite-preset-chip ${activePreset === preset.id ? 'active' : ''}`}
                    onClick={() => handlePresetSelect(preset.id)}
                  >
                    {uiText(preset.label)}
                  </button>
                ))}
                <button
                  type="button"
                  className={`invite-preset-chip ${activePreset === 'custom' ? 'active' : ''}`}
                  onClick={() => handlePresetSelect('custom')}
                >{uiText("Custom")}</button>
              </div>

              {showCustomPermissions && (
                <div className="invite-permissions-list">
                  {TEAM_PERMISSION_SECTIONS.map((section) => (
                    <div key={section.id} className="invite-permission-section">
                      <h4>{uiText(section.title)}</h4>
                      <div className="invite-checkbox-group">
                        {section.permissions.map((permission) => (
                          <label key={permission.key} className="invite-checkbox-item">
                            <input
                              type="checkbox"
                              checked={form.permissions[permission.key] || false}
                              onChange={() => handlePermissionToggle(permission.key)}
                            />
                            <span className="invite-checkbox-text">
                              <strong>{t(permission.titleKey || `team.permissions.${permission.key}.title`, permission.title)}</strong>
                              <small>{t(permission.descriptionKey || `team.permissions.${permission.key}.description`, permission.description)}</small>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {form.permissions.canEditCommunity && (
                <div className="invite-community-scope">
                  <label>{uiText("Community editor scope")}</label>
                  <div className="invite-chip-grid">
                    {normalizedCommunityChannelOptions.map((channel) => (
                      <button
                        key={channel.value}
                        type="button"
                        className={`invite-department-chip ${form.communityChannels.includes(channel.value) ? 'active' : ''}`}
                        onClick={() => handleCommunityChannelToggle(channel.value)}
                      >
                        {uiText(channel.label)}
                      </button>
                    ))}
                  </div>
                  <p className="invite-helper-text">{uiText("Choose which private community feeds this editor can manage.")}</p>
                </div>
              )}
            </div>
          </div>

          <div className="invite-modal-footer">
            <button type="button" onClick={onClose} className="invite-btn-secondary">{uiText("Cancel")}</button>
            <button
              type="submit"
              disabled={isLoading || !canSubmit}
              className="invite-btn-primary"
            >
              {isLoading ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : isEditMode ? (
                uiText("Save Access")
              ) : (
                uiText("Send Invite")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteMemberModal;
